// ═════════════════════════════════════════════════════════════════════════════
//  LA PARED DE REACTIVACIÓN
//
//  Adónde llega un alumno que se quedó sin acceso. No es una pantalla de
//  error: es el momento en que decide si paga, y está construida como tal.
//
//  ── UNA SOLA PANTALLA PARA LOS CUATRO FINALES (17-09-2026) ──────────────────
//  Antes había dos avisos distintos: este y un recuadro rojo de «Pago vencido»
//  dentro de `SubscriptionGuard`. Los dos se disparaban a la vez y el alumno
//  veía primero el rojo y después este, porque cada uno leía el estado por su
//  cuenta y no terminaban de cargar al mismo tiempo. Rob lo vio el 17-09-2026
//  con la cuenta de validación: «sale el banner que no me gusta por un tiempo
//  y se quita». El rojo se eliminó. Queda esta, y aquí dentro se elige QUÉ se
//  le dice según por qué se quedó fuera:
//
//    · prueba   — se le acabaron los días gratis;
//    · pago     — tenía un plan de pago y venció;
//    · sinPlan  — nunca tuvo plan que vencer;
//    · empresa  — su empresa dejó de incluirlo en el programa.
//
//  La diferencia la decide el BACKEND (`prueba_vencida`, `sin_plan`,
//  `empresa.baja`), no esta pantalla: repartida en dos sitios, el día que
//  cambie la regla se quedaría una de las dos sin cambiar.
//
//  ── EL MODELO ES NETFLIX ────────────────────────────────────────────────────
//  Cuando a Netflix le falla el cobro no te borra la cuenta ni te expulsa: te
//  deja entrar, te reconoce, te enseña que tu lista y tu historial siguen ahí,
//  y convierte TODA la aplicación en una sola pantalla con un solo botón. No
//  te regaña ni te habla de deuda. Lo que hace es recordarte lo que te estás
//  perdiendo y quitar de en medio cualquier otra cosa que puedas pulsar.
//
//  Aquí se copia esa forma:
//    · el alumno entra y sigue siendo él —su nivel y su XP se le enseñan—;
//    · nada de su progreso se borra, y se le dice explícitamente;
//    · hay UN botón que importa, y lleva a los planes;
//    · el tono no reclama nada: invita a no parar.
//
//  ── POR QUE UNA PAGINA Y NO UN AVISO FLOTANTE ───────────────────────────────
//  Un aviso encima de la pantalla se cierra, se ignora y deja ver por debajo lo
//  que ya no se puede usar. Aquí se cambia de pantalla. Traen a esta página dos
//  guardias, y los dos a propósito: `ProtectedRoute` aparta a quien se le acabó
//  la prueba —que no tiene ninguna suscripción que gestionar— y
//  `SubscriptionGuard` manda aquí todo `overdue` desde las rutas que protege.
//  El panel se queda abierto para quien sí tiene algo que renovar o activar: un
//  muro que también tapa la salida no vende nada.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useSubscriptionStatus } from "../components/guards/SubscriptionGuard";
import { duracionDeLaPrueba } from "../limites";
import "./ReactivarPage.css";

const WHATSAPP = "5215610480746";

export default function ReactivarPage() {
  const navigate = useNavigate();
  const {
    status, plan, expiryDate, xp, pruebaVencida, sinPlan, empresa, loading,
  } = useSubscriptionStatus();
  const [perfil, setPerfil] = useState(null);

  // Sin sesión no hay nada que reactivar.
  useEffect(() => {
    if (!localStorage.getItem("token") && !localStorage.getItem("access_token")) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Y quien YA tiene acceso no se queda encerrado aquí. Pasa de verdad: el
  // alumno paga, vuelve con el botón de atrás y esta página seguía puesta.
  useEffect(() => {
    if (!loading && status && status !== "overdue") {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, status, navigate]);

  // El nombre y el nivel son opcionales: si esto falla, la página se muestra
  // igual. Nunca se deja a alguien delante de un muro en blanco porque una
  // llamada secundaria no respondió.
  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("access_token");
    if (!token) return;
    fetch("/api/profile/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(setPerfil)
      .catch(() => setPerfil(null));
  }, []);

  const nombre = (perfil?.alias || perfil?.full_name || "").trim().split(" ")[0];
  const nivel = perfil?.learning_profile?.current_level || perfil?.current_level || null;

  // Se arma a mano: `toLocaleDateString` devolvia "04-septiembre" en Chromium,
  // que no es como se escribe una fecha en español.
  const fecha = (() => {
    const cuando = expiryDate || empresa?.vence || null;
    if (!cuando) return null;
    const d = new Date(String(cuando).slice(0, 10) + "T12:00:00");
    if (Number.isNaN(d.getTime())) return null;
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
                   "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    return `${d.getDate()} de ${meses[d.getMonth()]}`;
  })();

  // ── QUE SE LE DICE ─────────────────────────────────────────────────────────
  // «Tu pago venció, renueva» a quien nunca pagó es, además de falso, la peor
  // frase posible en el momento de convertir: suena a reclamo de deuda. Y al
  // empleado al que su empresa dio de baja tampoco se le debe nada.
  const motivo = empresa?.baja
    ? {
        clave:    "empresa",
        icono:    "🎓",
        etiqueta: empresa?.nombre
          ? `Tu programa con ${empresa.nombre} terminó`
          : "Tu programa de empresa terminó",
        pre:      nombre ? `${nombre}, sigue tu` : "Sigue tu",
        resalte:  "entrenamiento",
        post:     " por tu cuenta",
        sub:      "Tu empresa dejó de incluirte en el programa. Eso no borra nada " +
                  "de lo tuyo: tu nivel, tus XP y tus rachas siguen aquí.",
        cta:      "⚡ Ver los planes y seguir entrenando",
        // Sin fecha a propósito: `expiryDate` trae el fin del CONTRATO de la
        // empresa, que suele estar en el futuro. «Tu programa terminó el 12 de
        // octubre» a alguien al que dieron de baja hoy es sencillamente falso.
        pieFecha: "",
        pie:      "Sin contratos · Activación en minutos",
        whatsapp: "¡Hola! Terminó mi acceso por mi empresa y quiero seguir con un plan de NSL.",
      }
    : pruebaVencida
    ? {
        clave:    "prueba",
        icono:    "⏳",
        etiqueta: `Tu prueba de ${duracionDeLaPrueba()} terminó`,
        pre:      nombre ? `${nombre}, no pares tu` : "No pares tu",
        resalte:  "entrenamiento",
        post:     "",
        sub:      "Acabas de empezar a soltarte. Los reflejos para hablar sin pensar se " +
                  "construyen entrenando seguido — y se pierden parando.",
        cta:      "⚡ Elegir mi plan y seguir entrenando",
        pieFecha: fecha ? `Tu prueba${plan ? ` de ${plan}` : ""} terminó el ${fecha}. ` : "",
        pie:      "Sin contratos · Activación en minutos",
        whatsapp: "¡Hola! Se me acabó la prueba gratuita de NSL y quiero contratar un plan.",
      }
    // `!expiryDate` entra aquí a propósito: un plan de pago vencido SIEMPRE
    // trae su fecha. Si no hay fecha, no hay nada que se haya vencido, y
    // decirle «tu plan terminó» sería inventarle una deuda.
    : (sinPlan || !expiryDate)
    ? {
        clave:    "sinPlan",
        icono:    "🚀",
        etiqueta: "Tu cuenta no tiene plan",
        pre:      nombre ? `${nombre}, elige tu` : "Elige tu",
        resalte:  "plan",
        post:     " y empieza",
        sub:      "Con un plan entras a todo: Spacy, el entrenamiento completo de " +
                  "A1 a C2, el glosario y los minijuegos.",
        cta:      "⚡ Ver los planes",
        pieFecha: "",
        pie:      "Sin contratos · Activación en minutos",
        whatsapp: "¡Hola! Quiero contratar un plan de NSL.",
      }
    : {
        clave:    "pago",
        icono:    "⌛",
        etiqueta: "Tu plan terminó",
        pre:      nombre ? `${nombre}, no pares tu` : "No pares tu",
        resalte:  "entrenamiento",
        post:     "",
        sub:      "Llevas tiempo soltándote. Los reflejos para hablar sin pensar se " +
                  "mantienen entrenando seguido — y se pierden parando.",
        cta:      "⚡ Renovar mi plan y seguir entrenando",
        pieFecha: fecha ? `Tu plan${plan ? ` ${plan}` : ""} terminó el ${fecha}. ` : "",
        pie:      "Renuevas en minutos · Sin contratos",
        whatsapp: `¡Hola! Quiero renovar mi plan de NSL${plan ? ` (${plan})` : ""}.`,
      };

  // «Todo tu avance sigue guardado» con 0 XP y sin nivel es una caja vacía que
  // presume de nada. A quien nunca tuvo plan no se le enseña.
  const hayAvance = Boolean(nivel) || xp > 0;
  const muestraGuardado = motivo.clave !== "sinPlan" || hayAvance;

  function haciaWhatsApp() {
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(motivo.whatsapp)}`, "_blank");
  }

  function cerrarSesion() {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    navigate("/login", { replace: true });
  }

  return (
    <div className="rcv-root">
      <Header />

      <main className="rcv-main">
        <div className="rcv-card" data-motivo={motivo.clave}>
          <div className="rcv-icono" aria-hidden="true">{motivo.icono}</div>

          <div className="rcv-etiqueta">{motivo.etiqueta}</div>

          <h1 className="rcv-titulo">
            {motivo.pre} <span className="rcv-resalte">{motivo.resalte}</span>{motivo.post}
          </h1>

          <p className="rcv-sub">{motivo.sub}</p>

          {/* Lo que NO se pierde. Es la mitad del mensaje: quitar el miedo a
              que volver signifique empezar de cero. */}
          {muestraGuardado && (
            <div className="rcv-guardado">
              <div className="rcv-guardado-titulo">Todo tu avance sigue guardado</div>
              <div className="rcv-guardado-items">
                {nivel && (
                  <div className="rcv-dato">
                    <span className="rcv-dato-cifra">{nivel}</span>
                    <span className="rcv-dato-texto">tu nivel</span>
                  </div>
                )}
                {xp > 0 && (
                  <div className="rcv-dato">
                    <span className="rcv-dato-cifra">{Number(xp).toLocaleString("es-MX")}</span>
                    <span className="rcv-dato-texto">XP ganados</span>
                  </div>
                )}
                <div className="rcv-dato">
                  <span className="rcv-dato-cifra">100%</span>
                  <span className="rcv-dato-texto">de tu progreso</span>
                </div>
              </div>
              <div className="rcv-guardado-pie">
                Cuando actives tu plan, vuelves justo donde lo dejaste.
              </div>
            </div>
          )}

          <button className="rcv-cta" onClick={() => navigate("/plans")}>
            {motivo.cta}
          </button>

          <button className="rcv-cta-sec" onClick={haciaWhatsApp}>
            💬 Tengo dudas · hablar por WhatsApp
          </button>

          <div className="rcv-pie">
            {motivo.pieFecha && <span>{motivo.pieFecha}</span>}
            {motivo.pie}
          </div>

          <button className="rcv-salir" onClick={cerrarSesion}>
            Cerrar sesión
          </button>
        </div>
      </main>
    </div>
  );
}
